import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { thumbhashToDataUrl } from "@/shared/lib/thumbhash";
import type { ImageJobOutput, ImageJobPhase } from "../hooks/useImageJobStatus";
import { ImageIcon } from "./icons";
import { ImageLightbox } from "./ImageLightbox";

/**
 * The placeholder box matches the shape of the image that's coming: the tool
 * reports the resolved ratio (e.g. "16:9", "1:1", "9:16") the instant the job
 * is queued, so the loader is sized correctly from the first frame and the
 * produced image fills it with no reshape. Once the real output lands we use its
 * exact width/height so the box matches the bytes precisely. Falls back to
 * square when nothing is known yet.
 */
function aspectRatio(output: ImageJobOutput | null, requestedRatio?: string | null): string {
  if (output?.width && output.height) return `${output.width} / ${output.height}`;
  if (requestedRatio) {
    const [w, h] = requestedRatio.split(":");
    if (w && h) return `${w} / ${h}`;
  }
  return "1 / 1";
}

export interface ImageResultCardProps {
  phase: ImageJobPhase;
  output: ImageJobOutput | null;
  /** Resolved ratio ("16:9" | "1:1" | "9:16" | …) so the loader is sized before the image lands. */
  requestedRatio?: string | null;
}

/**
 * The visual result of an in-chat `generate_image` job: an image-shaped box that
 * starts as a muted skeleton block on a gentle pulse, then fades in the produced
 * image (over its ThumbHash blur) once the job succeeds. The finished image is
 * clickable — it opens a full-screen lightbox, the same way the library does. On
 * failure/timeout it shows a quiet "couldn't generate" state in the same box.
 * Capped to a comfortable width so it sits inside the chat bubble.
 */
export function ImageResultCard({ phase, output, requestedRatio }: ImageResultCardProps) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const ratio = aspectRatio(output, requestedRatio);
  const placeholder = thumbhashToDataUrl(output?.thumbhash);
  const src = output?.url;
  const failed = phase === "failed" || imgError;
  const ready = Boolean(src) && phase === "succeeded" && !imgError;
  const filename = `anyai-${output?.id ?? "image"}.${output?.format ?? "png"}`;

  return (
    <>
      <div
        data-testid="image-result-card"
        data-state={phase}
        style={{ aspectRatio: ratio }}
        className="relative w-full max-w-[340px] overflow-hidden rounded-md bg-canvas-soft"
      >
        {placeholder && !failed ? (
          <img
            src={placeholder}
            alt=""
            aria-hidden="true"
            className={cn(
              "absolute inset-0 size-full scale-110 object-cover blur-2xl transition-opacity duration-300",
              loaded ? "opacity-0" : "opacity-100",
            )}
          />
        ) : null}

        {ready && src ? (
          <button
            type="button"
            onClick={() => setZoomed(true)}
            aria-label={t("chat.toolConfirm.imageGenerated")}
            className="absolute inset-0 cursor-zoom-in outline-none focus-visible:ring-[3px] focus-visible:ring-tint"
          >
            <img
              src={src}
              alt={t("chat.toolConfirm.imageGenerated")}
              loading="lazy"
              ref={(node) => {
                if (node?.complete) setLoaded(true);
              }}
              onLoad={() => setLoaded(true)}
              onError={() => setImgError(true)}
              className={cn(
                "size-full object-cover transition-opacity duration-300",
                loaded ? "opacity-100" : "opacity-0",
              )}
            />
          </button>
        ) : null}

        {phase === "generating" && !failed ? (
          <span className="absolute inset-0 animate-pulse bg-tint" aria-hidden="true" />
        ) : null}

        {failed ? (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-3 text-center text-ink-faint [&_svg]:size-6">
            <ImageIcon />
            <span className="font-sans text-[11.5px]">{t("chat.toolConfirm.imageFailed")}</span>
          </span>
        ) : null}
      </div>

      {zoomed && src ? (
        <ImageLightbox
          src={src}
          alt={t("chat.toolConfirm.imageGenerated")}
          filename={filename}
          onClose={() => setZoomed(false)}
        />
      ) : null}
    </>
  );
}
